import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Download, Loader2, AlertTriangle, Gift, Copy } from "lucide-react";
import { toast } from "sonner";
import api, { API } from "../lib/apiClient";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";

const MAX_POLLS = 12;

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const { clearCart } = useCart();
  const { user } = useAuth();
  const [status, setStatus] = useState("checking"); // checking | paid | timeout | error
  const [order, setOrder] = useState(null);
  const [referral, setReferral] = useState(null);
  const cleared = useRef(false);

  useEffect(() => {
    if (status === "paid" && user) {
      api.get("/referral/me").then((r) => setReferral(r.data)).catch(() => {});
    }
  }, [status, user]);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let polls = 0;
    let active = true;

    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (!active) return;
        if (data.payment_status === "paid") {
          setOrder(data.order);
          setStatus("paid");
          if (!cleared.current) {
            clearCart();
            cleared.current = true;
          }
          return;
        }
        if (data.status === "expired" || data.payment_status === "failed") {
          setStatus("error");
          return;
        }
        polls += 1;
        if (polls >= MAX_POLLS) {
          setStatus("timeout");
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        if (!active) return;
        setStatus("error");
      }
    };
    poll();
    return () => { active = false; };
  }, [sessionId, clearCart]);

  if (status === "checking")
    return (
      <div data-testid="payment-checking" className="flex min-h-[60vh] flex-col items-center justify-center px-5 text-center">
        <Loader2 className="h-10 w-10 animate-spin text-signal" />
        <h1 className="mt-6 font-serif text-3xl font-light tracking-tighter text-obsidian">Confirming your payment…</h1>
        <p className="mt-2 text-sm text-obsidian/60">Hang tight, this only takes a moment.</p>
      </div>
    );

  if (status !== "paid")
    return (
      <div data-testid="payment-error" className="flex min-h-[60vh] flex-col items-center justify-center px-5 text-center">
        <AlertTriangle className="h-10 w-10 text-signal" />
        <h1 className="mt-6 font-serif text-3xl font-light tracking-tighter text-obsidian">
          {status === "timeout" ? "Still processing" : "Something went wrong"}
        </h1>
        <p className="mt-2 max-w-md text-sm text-obsidian/60">
          {status === "timeout"
            ? "Your payment is taking longer than usual. If you were charged, your order will appear in your account shortly."
            : "We couldn't confirm this payment. If money was taken, contact support with your session id."}
        </p>
        <Link to="/" className="mt-8 bg-signal px-6 py-3 text-xs font-semibold uppercase tracking-[0.15em] text-white transition-colors hover:bg-red-700">
          Back to shop
        </Link>
      </div>
    );

  return (
    <div data-testid="order-confirmation" className="mx-auto max-w-2xl px-5 py-20 md:px-8">
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="border border-obsidian/15 bg-white p-10 text-center">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-signal">
          <Check className="h-8 w-8 text-white" />
        </div>
        <h1 className="mt-6 font-serif text-4xl font-light tracking-tighter text-obsidian">Payment successful</h1>
        {order && (
          <>
            <p className="mt-3 text-obsidian/60">
              Thanks{order.name && order.name !== "Guest" ? `, ${order.name}` : ""}! A receipt was sent to{" "}
              <span className="font-medium text-obsidian">{order.email}</span>.
            </p>
            <div className="mt-6 inline-block border border-obsidian/15 px-6 py-3">
              <p className="text-xs uppercase tracking-[0.2em] text-obsidian/50">Order number</p>
              <p data-testid="order-number" className="font-serif text-2xl tracking-tighter text-obsidian">{order.order_number}</p>
            </div>
            <div className="mt-8 space-y-2 text-left">
              {order.items.map((it) => (
                <div key={it.product_id} className="flex items-center justify-between border border-obsidian/10 p-3">
                  <span className="text-sm text-obsidian/80">{it.title}</span>
                  <a
                    href={`${API}/orders/${order.id}/download/${it.product_id}`}
                    data-testid={`download-${it.product_id}`}
                    className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.15em] text-signal hover:underline"
                  >
                    <Download className="h-4 w-4" /> Download
                  </a>
                </div>
              ))}
            </div>
          </>
        )}
        <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-center">
          {user && (
            <Link to="/account" data-testid="view-orders-btn" className="border border-obsidian px-6 py-3 text-xs font-semibold uppercase tracking-[0.15em] text-obsidian transition-colors hover:bg-obsidian hover:text-white">
              View my orders
            </Link>
          )}
          <Link to="/" className="bg-signal px-6 py-3 text-xs font-semibold uppercase tracking-[0.15em] text-white transition-colors hover:bg-red-700">
            Keep shopping
          </Link>
        </div>

        {/* Refer & earn — turn this buyer into a promoter */}
        {referral ? (
          <div data-testid="success-referral" className="mt-8 border border-obsidian/15 bg-bone p-6 text-left">
            <div className="flex items-center gap-2 text-signal">
              <Gift className="h-5 w-5" />
              <span className="text-xs font-semibold uppercase tracking-[0.2em]">Earn {referral.rate}% — share your link</span>
            </div>
            <p className="mt-2 text-sm text-obsidian/60">
              Loved it? Share LuxDrop with other freelancers. They get {referral.welcome_value || 25}% off, you earn {referral.rate}% on every sale.
            </p>
            <div className="mt-4 flex items-center gap-2">
              <input
                data-testid="success-referral-link"
                readOnly
                value={referral.link}
                className="min-w-0 flex-1 border border-obsidian/20 bg-white px-3 py-2.5 text-sm text-obsidian/80 focus:outline-none"
              />
              <button
                data-testid="success-referral-copy"
                onClick={() => { navigator.clipboard.writeText(referral.link); toast.success("Referral link copied!"); }}
                className="inline-flex shrink-0 items-center gap-2 bg-signal px-4 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] text-white transition-colors hover:bg-red-700"
              >
                <Copy className="h-4 w-4" /> Copy
              </button>
            </div>
          </div>
        ) : (
          !user && (
            <div data-testid="success-referral-cta" className="mt-8 border border-obsidian/15 bg-bone p-6 text-left">
              <div className="flex items-center gap-2 text-signal">
                <Gift className="h-5 w-5" />
                <span className="text-xs font-semibold uppercase tracking-[0.2em]">Earn 20% per referral</span>
              </div>
              <p className="mt-2 text-sm text-obsidian/60">
                <Link to="/register" className="font-semibold text-signal hover:underline">Create a free account</Link> to get your personal referral link — share LuxDrop and earn 20% on every sale.
              </p>
            </div>
          )
        )}
      </motion.div>
    </div>
  );
}
